import { PaginationNavigationButton, PaginationNumber } from '@frontend/components/list';
import { useMemo } from 'react';
import { LuChevronLeft, LuChevronRight } from 'react-icons/lu';
import { FormattedMessage, useIntl } from 'react-intl';

export interface InsightClientListPaginationProps {
    totalCount: number;
    pageIndex: number;
    pageCount: number;
    onPageChange: (page: number) => void;
    /** Prefix for pagination control ids (must be unique per list on the page). */
    navigationId: string;
}

/** Client-side pagination for chart-backed ranked insight lists (no server page/size). */
export const InsightClientListPagination = ({ totalCount, pageIndex, pageCount, onPageChange, navigationId }: InsightClientListPaginationProps) => {
    const intl = useIntl();
    const previousPageLabel = intl.formatMessage({
        id: 'pagination.previousPage',
        description: 'The label for the previous page button',
        defaultMessage: 'Previous page'
    });
    const nextPageLabel = intl.formatMessage({
        id: 'pagination.nextPage',
        description: 'The label for the next page button',
        defaultMessage: 'Next page'
    });

    const pagesToDisplay = useMemo(() => {
        if (pageCount <= 5) {
            return Array.from({ length: pageCount }, (_, i) => i + 1);
        }
        const current = pageIndex + 1;
        const pages = new Set<number>([1, pageCount, current, current - 1, current + 1]);
        return [...pages].filter((page) => page >= 1 && page <= pageCount).sort((a, b) => a - b);
    }, [pageCount, pageIndex]);

    if (pageCount <= 1) {
        return (
            <div className='border-border-default flex w-full items-center border-t pt-4'>
                <div className='text-foreground-default flex gap-1 text-lg font-semibold'>
                    <span>{totalCount}</span>
                    <FormattedMessage
                        id='table.results'
                        description='The label for results'
                        defaultMessage='results'
                    />
                </div>
            </div>
        );
    }

    const backDisabled = pageIndex <= 0;
    const forwardDisabled = pageIndex + 1 >= pageCount;

    return (
        <div className='border-border-default flex w-full flex-wrap items-center justify-between gap-3 border-t pt-4'>
            <div className='text-foreground-default flex gap-1 text-lg font-semibold'>
                <span>{totalCount}</span>
                <FormattedMessage
                    id='table.results'
                    description='The label for results'
                    defaultMessage='results'
                />
            </div>
            <ul className='m-0 inline-flex items-center gap-3'>
                <PaginationNavigationButton
                    id={`${navigationId}-prev`}
                    disabled={backDisabled}
                    onClick={() => onPageChange(pageIndex - 1)}
                    ariaLabel={previousPageLabel}>
                    <LuChevronLeft className={backDisabled ? 'text-border-default text-base' : 'text-brand-muted text-base'} />
                </PaginationNavigationButton>
                {pagesToDisplay.map((page) => (
                    <PaginationNumber
                        key={page}
                        id={navigationId}
                        index={page}
                        highlighted={pageIndex + 1 === page}
                        onClick={(i) => onPageChange(i - 1)}
                        pageCount={pageCount}
                    />
                ))}
                <PaginationNavigationButton
                    id={`${navigationId}-next`}
                    disabled={forwardDisabled}
                    onClick={() => onPageChange(pageIndex + 1)}
                    ariaLabel={nextPageLabel}>
                    <LuChevronRight className={forwardDisabled ? 'text-border-default text-base' : 'text-brand-muted text-base'} />
                </PaginationNavigationButton>
            </ul>
        </div>
    );
};
